import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Notiz } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useFormularZustand, useBestaetigung }
  from "@kundeninfo/ui";
import { Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { relativeZeit } from "../format.js";
import { KiKnopf, LoeschKnopf } from "../components/meHuellen.js";
import { useJumpFocus } from "../components/jumpFocus.js";
import { renderMarkdown } from "../markdown.js";
import { UnternehmensChipsAnzeige, UnternehmensSchalter, ZuordnungsPanel, gleicheMenge }
  from "./UnternehmensZuordnung.js";
import type { ZuordnungsUmfeld } from "./UnternehmensZuordnung.js";
import "./KundeNotizen.css";

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster aus KundeAnsprechpartner.tsx). */
export type NotizenClient = Pick<ApiClient,
  "listNotes" | "createNote" | "updateNote" | "deleteNote" | "setNoteCompanies">;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird (erst in T11). */
export type NotizenUmfeld = {
  client: NotizenClient;
  /** Id des angemeldeten Benutzers — entscheidet zusammen mit `istAdmin`, wer eine Notiz
   *  bearbeiten/löschen darf (der API-Wächter sitzt zusätzlich in der Route). */
  angemeldeterBenutzerId: string;
  istAdmin: boolean;
};

/** `unternehmen`: die Zuordnung zu Unternehmen (Mandantentrennung R12, R13) — Kontext, kein Filter.
 *  `oeffneNotizId`: die Notiz, die ein Sprung aus der Kundenübersicht in den Blick rollen und fokussieren soll. */
export type KundeNotizenProps = NotizenUmfeld & {
  kundeId: string; unternehmen: ZuordnungsUmfeld; oeffneNotizId?: string | null;
};

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError && fehler.message.trim() !== "") return fehler.message;
  return standard;
}

/**
 * Der Notiz-Reiter: ein Journal (neueste oben) mit gerendertem Markdown je Eintrag. Eine
 * eigene Notiz (oder jede, wenn ADMIN) trägt Bearbeiten/Löschen; Ändern schickt die gelesene
 * Version als optimistische Sperre mit. Der Inhalt läuft IMMER durch den sanitisierenden
 * Renderer (renderMarkdown) — nie als rohes HTML.
 *
 * Anlegen und Bearbeiten laufen im strikten Modalpanel (Hausregel, Entscheid Dirk vom
 * 06.09.2026). Vorher standen sie als offene Kästen im Journal: Ein Klick auf „Bearbeiten"
 * an einer anderen Notiz warf den halb getippten Text weg, und ein Wechsel des Reiters
 * ebenso — beides ohne Rückfrage. Genau davor schützt der Rahmen mit seinen zwei Ausgängen.
 *
 * Eigene Wurzelklasse `.kn` (NICHT `.ka` teilen — Kollisions-Lehre 01.09., siehe `.ka-neu`
 * in KundeAnlegen).
 */
export function KundeNotizen(p: KundeNotizenProps): ReactElement {
  const { client, kundeId, angemeldeterBenutzerId, istAdmin } = p;

  const [notizen, setNotizen] = useState<Notiz[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Rückmeldung außerhalb der Masken — ein Fehler (Löschen) als `fehler` (role alert),
   *  eine Auskunft („inzwischen gelöscht“) als `info` (Fixrunde 1, H5). */
  const [hinweis, setHinweis] = useState<{ text: string; art: "info" | "fehler" } | null>(null);
  // null = kein Anlege-Feld offen; eine Zeichenkette = das Feld ist offen mit diesem Entwurf.
  const [anlegenEntwurf, setAnlegenEntwurf] = useState<string | null>(null);
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null);
  const [bearbeiteEntwurf, setBearbeiteEntwurf] = useState("");
  /** Die Unternehmen der neuen Notiz — vorbelegt mit dem aktiven. */
  const vorbelegung = p.unternehmen.aktivId === null ? [] : [p.unternehmen.aktivId];
  const [anlegenFirmen, setAnlegenFirmen] = useState<string[]>([]);
  /** Die Notiz, deren Unternehmen gerade im eigenen Panel neu zugeordnet werden. */
  const [zuordnungFuer, setZuordnungFuer] = useState<Notiz | null>(null);
  /** „Meine Eingaben behalten“: nur die Versionsbasis vorrücken (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const { items } = await client.listNotes(kundeId);
      setNotizen(items);
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Notizen konnten nicht geladen werden."));
    }
  }, [client, kundeId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  useJumpFocus("data-note-id", p.oeffneNotizId, notizen);

  function darfBearbeiten(n: Notiz): boolean {
    return n.authorUserId === angemeldeterBenutzerId || istAdmin;
  }

  // ISO-Zeitstempel sortieren lexikografisch = chronologisch → neueste zuerst.
  const sortiert = [...(notizen ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  /**
   * Formularzustände beider Masken (ME-Etappe 3, Aufgabe 3) — es steht immer nur eine
   * offen. `laeuft` sperrt das Feld, eine 422 hängt am Feld, eine 409 beim Ändern hält den
   * Text als „fremder Stand“ fest. Früher schloss sie die Maske und lud neu — der getippte
   * Text war weg. „Neu laden“ übernimmt jetzt den Serverstand, „Meine Eingaben behalten“
   * nur dessen Version (die Notiz kommt frisch aus der Liste, siehe `bearbeitete`).
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      if (anlegenEntwurf !== null) {
        await client.createNote(kundeId, { contentMarkdown: anlegenEntwurf, companyIds: anlegenFirmen });
        return true;
      }
      const n = sortiert.find((x) => x.id === bearbeiteId);
      if (n === undefined) throw new Error("Die Notiz ist nicht mehr da.");
      // expectedVersion = die gelesene Version: Ist der Datensatz zwischenzeitlich fremd
      // geändert worden, antwortet die API mit 409 statt still zu überschreiben.
      await client.updateNote(kundeId, n.id, n.version, { contentMarkdown: bearbeiteEntwurf });
      return true;
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      const { items } = await client.listNotes(kundeId);
      setNotizen(items);
      const frisch = items.find((x) => x.id === bearbeiteId);
      if (frisch === undefined) {
        setBearbeiteId(null);
        setHinweis({ text: "Diese Notiz wurde inzwischen gelöscht.", art: "info" });
        return;
      }
      if (!behalten) setBearbeiteEntwurf(frisch.contentMarkdown);
    },
    bekannteFelder: ["contentMarkdown"],
  });
  const speichert = formular.laeuft;
  const textFehler = formular.feldFehler.contentMarkdown;

  function anlegenOeffnen(): void {
    formular.zuruecksetzen();
    setAnlegenFirmen(vorbelegung);
    setAnlegenEntwurf("");
  }

  async function speichern(): Promise<void> {
    const text = anlegenEntwurf ?? bearbeiteEntwurf;
    if (text.trim() === "" || speichert) return;
    const ok = await formular.aktion();
    if (ok !== true) return;
    setAnlegenEntwurf(null);
    setBearbeiteId(null);
    setHinweis(null);
    await laden();
  }

  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  function bearbeitenStarten(n: Notiz): void {
    formular.zuruecksetzen();
    setBearbeiteId(n.id);
    setBearbeiteEntwurf(n.contentMarkdown);
    setHinweis(null);
  }

  const { bestaetigen } = useBestaetigung();
  async function loeschen(n: Notiz): Promise<void> {
    if (!(await bestaetigen({
      titel: "Notiz löschen?", text: `Diese Notiz von ${n.authorName} wirklich löschen?`,
      bestaetigenText: "Löschen", gefaehrlich: true,
    }))) return;
    try {
      await client.deleteNote(kundeId, n.id);
      setHinweis(null);
      await laden();
    } catch (e) {
      setHinweis({ text: alsMeldung(e, "Die Notiz konnte nicht gelöscht werden."), art: "fehler" });
    }
  }


  /**
   * Die Notiz, die gerade bearbeitet wird — Quelle für den Vergleich, die Version UND für
   * die Frage, ob eine Maske offen steht.
   *
   * Die EINE Quelle ist Absicht (Fixrunde 1, 07.09.2026): Sperrte der Knopf „Neue Notiz"
   * sich an `bearbeiteId`, während das Panel an dieser Notiz hängt, gäbe es ein Fenster, in
   * dem beides auseinanderfällt — eine Kennung, zu der die Liste (nach einem Neuladen, nach
   * einem fremden Löschen) keine Notiz mehr führt. Dann stünde kein Panel, aber der Knopf
   * bliebe stillgelegt: eine Sackgasse, aus der nur der Reiterwechsel hilft.
   */
  const bearbeitete = sortiert.find((n) => n.id === bearbeiteId) ?? null;

  /**
   * Der EINE Ausgang der beiden Masken neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald
   * etwas Ungespeichertes darin steht.
   *
   * „Etwas darin" heisst beim ANLEGEN: irgendein Text (der Stand beim Öffnen ist leer);
   * beim BEARBEITEN: ein anderer Text als der gespeicherte. Ein eigener Ausgangsstand wird
   * dafür nicht mitgeführt — die Notiz selbst ist er.
   */
  async function anlegenAbbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen((anlegenEntwurf ?? "").trim() !== ""
      || !gleicheMenge(anlegenFirmen, vorbelegung)))) return;
    setAnlegenEntwurf(null);
  }

  async function bearbeitenAbbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(bearbeitete !== null
      && bearbeiteEntwurf !== bearbeitete.contentMarkdown))) return;
    setBearbeiteId(null);
  }

  /** Steht gerade eine der beiden Masken offen? */
  const maskeOffen = anlegenEntwurf !== null || bearbeitete !== null || zuordnungFuer !== null;

  /** Die Rückmeldung des Schreibwegs — im Panel, solange eines steht. */
  function hinweisband(): ReactElement | null {
    if (formular.meldung === null) return null;
    return <Hinweis art="fehler">{formular.meldung}</Hinweis>;
  }

  /** Die Hinweiszeile „fremder Stand“ (nur beim Ändern möglich). */
  function konfliktText(): ReactElement | undefined {
    if (formular.zustand !== "fremder-stand") return undefined;
    return (
      <span>
        Diese Notiz wurde zwischenzeitlich geändert. Dein Text steht noch da — „Neu laden“
        übernimmt den aktuellen Stand.{" "}
        <KiKnopf className="ki-eingaben-behalten" onClick={eigeneEingabenBehalten}>
          Meine Eingaben behalten
        </KiKnopf>
      </span>
    );
  }

  /** Die Vorschau unter dem Feld — dieselbe in beiden Masken, damit sie nicht auseinander
   *  laufen. Sie steht nur bei Inhalt: Eine Überschrift „Vorschau" über nichts ist eine
   *  Zeile, die nur Platz kostet. */
  function vorschau(text: string): ReactElement | null {
    if (text.trim() === "") return null;
    return (
      <div className="kn__vorschau">
        <span className="kn__vorschau-label">Vorschau</span>
        {renderMarkdown(text)}
      </div>
    );
  }

  return (
    <div className="kn">
      <div className="kn__neu-bereich">
        {/* me-knopf (ME-Etappe 4, P4). */}
        <KiKnopf art="primaer" className="kn__neu" onClick={anlegenOeffnen}
          deaktiviert={maskeOffen}>
          Neue Notiz
        </KiKnopf>
      </div>

      {anlegenEntwurf !== null && (
        <Modal titel="Neue Notiz" breite="mittel"
          onAbbrechen={anlegenAbbrechen} abbrechenGesperrt={speichert}>
          <Formular beschriftung="oben" {...formular.formularProps}>
            {/* `Feld typ="mehrzeilig"` (me-eingabe) — wie beim Bearbeiten. */}
            <Feld id="kn-neu-feld" label="Neue Notiz (Markdown)" typ="mehrzeilig" bearbeitbar
              wert={anlegenEntwurf} fehler={textFehler}
              onChange={(wert) => setAnlegenEntwurf(wert)} />
            {vorschau(anlegenEntwurf)}
            <UnternehmensSchalter umfeld={p.unternehmen} wert={anlegenFirmen} onWert={setAnlegenFirmen} />
          </Formular>
          {hinweisband()}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={anlegenEntwurf.trim() === "" || formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={anlegenAbbrechen} />
        </Modal>
      )}

      {bearbeitete !== null && (
        <Modal titel="Notiz bearbeiten" breite="mittel"
          onAbbrechen={bearbeitenAbbrechen} abbrechenGesperrt={speichert}>
          <Formular beschriftung="oben" {...formular.formularProps} hinweis={konfliktText()}>
            {/* „Notiz (Markdown)" und nicht mehr „Notiz bearbeiten": Der Panel-Titel sagt
                bereits, dass hier bearbeitet wird — trüge das Feld denselben Namen, gäbe es
                zwei Elemente mit gleichem zugänglichen Namen (Panel und Feld), und eine
                Suche danach träfe beide. Der Feldname sagt jetzt, WAS darin steht.

                `Feld typ="mehrzeilig"` (me-eingabe): Seit me-design c9c0c08 behält der
                Anfangswert einer Markdown-Notiz seine Zeilen (Befund 2) — vorher ein rohes
                <textarea>. Befund und Sperre bringt das Element. */}
            <Feld id="kn-bearbeiten-feld" label="Notiz (Markdown)" typ="mehrzeilig"
              zeilen={8} bearbeitbar wert={bearbeiteEntwurf} fehler={textFehler}
              onChange={(wert) => setBearbeiteEntwurf(wert)} />
            {vorschau(bearbeiteEntwurf)}
          </Formular>
          {hinweisband()}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={bearbeiteEntwurf.trim() === "" || formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }}
            onAbbrechen={bearbeitenAbbrechen} />
        </Modal>
      )}

      {/* Die Unternehmen einer Notiz: eigenes Panel, eigener Schreibweg (die Menge als Ganzes,
          R12) — das Bearbeiten des Texts bleibt davon unberührt. */}
      {zuordnungFuer !== null && (
        <ZuordnungsPanel titel="Unternehmen der Notiz" was="Notiz" umfeld={p.unternehmen}
          ausgang={zuordnungFuer.companyIds}
          onSpeichern={async (ids) => {
            await client.setNoteCompanies(kundeId, zuordnungFuer.id, ids);
            setZuordnungFuer(null);
            setHinweis(null);
            await laden();
          }}
          onAbbrechen={() => setZuordnungFuer(null)} />
      )}

      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {/* Der Hinweis steht dort, wo gerade gearbeitet wird: bei offener Maske IM Panel
          (siehe oben), sonst hier. Hinter dem Schleier wäre die Auskunft „konnte nicht
          gespeichert werden" sichtbar, aber nicht zu befolgen — der Text, um den es geht,
          läge davor. */}
      {!maskeOffen && hinweis !== null && <Hinweis art={hinweis.art}>{hinweis.text}</Hinweis>}

      {notizen !== null && sortiert.length === 0 && (
        <p className="kn__leer">Noch keine Notizen zu diesem Kunden.</p>
      )}

      {/* Das Journal bleibt IMMER lesbar — auch hinter einem offenen Panel: Es ist der
          Zusammenhang, in dem gerade geschrieben wird. Der Eintrag zeigt dabei weiterhin
          seinen gespeicherten Stand, nicht den Entwurf; was im Panel steht, ist noch
          nicht die Notiz.

          Seine AKTIONEN sind dabei gesperrt (`maskeOffen`, Fixrunde 1 vom 07.09.2026): Sie
          liegen hinter dem Schleier und wären Knöpfe, die niemand drücken kann. Der Riegel
          sagt das nicht nur — er hält auch die Tests ehrlich: In jsdom fängt kein Element
          Zeigerereignisse ab, ein Klick „hinter" das Panel ginge dort durch und liesse eine
          klemmende Maske grün aussehen. */}
      <ul className="kn__journal">
        {sortiert.map((n) => (
          <li key={n.id} className="kn__eintrag" tabIndex={-1} data-note-id={n.id}>
            <div className="kn__kopf">
              <span className="kn__autor">{n.authorName}</span>
              <UnternehmensChipsAnzeige alle={p.unternehmen.alle} companyIds={n.companyIds}
                className="kn__firmen" />
              <span className="kn__zeit">
                {relativeZeit(n.createdAt)}
                {n.updatedAt !== n.createdAt && <span className="kn__geaendert"> · bearbeitet</span>}
              </span>
            </div>

            <div className="kn__inhalt">{renderMarkdown(n.contentMarkdown)}</div>

            {darfBearbeiten(n) && (
              <div className="kn__aktionen">
                <KiKnopf deaktiviert={maskeOffen} onClick={() => bearbeitenStarten(n)}>
                  Bearbeiten
                </KiKnopf>
                <KiKnopf deaktiviert={maskeOffen} onClick={() => setZuordnungFuer(n)}>
                  Unternehmen zuordnen
                </KiKnopf>
                <LoeschKnopf deaktiviert={maskeOffen} name={`Notiz von ${n.authorName} löschen`}
                  onClick={() => void loeschen(n)} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
